import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import createClient, { type Client, type Middleware } from 'openapi-fetch';

import { AuthService } from 'src/app/auth/services/auth.service';
import { ErrorReportingService } from 'src/app/features/shared/error/error-reporting.service';
import { IResponseData } from '../../models/response-data';
import { paths } from 'src/assets/api/schema';
import { ConfigService } from '../config-services/config.service';
import { RequestLoadingService } from '../loading-services/request-loading.service';
import { ResponseStateService } from '../response-state/response-state.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';

// Opt-in header voor aanroepen die een VERWACHTE fout-status (bv. 403 bij een capability-probe)
// bewust niet als gebruikersnotificatie willen tonen — zie onResponse hieronder. Puur
// client-side signaal (nooit door de backend gelezen); CORS staat AllowAnyHeader() toe
// (Program.cs), dus geen impact op de preflight.
export const API_SUPPRESS_ERROR_HEADER = 'X-Suppress-Error-Notification';

// Opt-in header voor achtergrond-probes die de gedeelde, sessie-brede state
// (domainProfile/claims/domainUserData, zie ResponseStateService.apply) NIET mogen beïnvloeden —
// hun eigen respons zegt niets over "welk domain/pagina is nu in beeld" (bv. de eenmalige
// is-admin/account-email-checks in ApplicationUserService), maar liep via de gedeelde
// ApiService-middleware toch mee in apply()'s onvoorwaardelijke domainProfile_set(null) zodra hun
// respons toevallig ná een gelijktijdige, wél-relevante pagina-fetch binnenkwam — een last-write-
// wins race die de header blijvend het verkeerde (generieke) logo liet tonen na opnieuw inloggen.
// Puur client-side signaal, nooit door de backend gelezen.
export const API_SUPPRESS_STATE_APPLY_HEADER = 'X-Suppress-State-Apply';

// Opt-in header voor responses waarvan de domainUserDto/projectMemberDto NIET de "huidig bezochte
// domain/project" voor de rest van de app voorstelt (bv. AdminPanel/details, dat domein 0 — de
// ingelogde admin's eigen Reflecs-lidmaatschap — meestuurt puur om zijn eigen claims op te
// scopen). Zonder dit zou ResponseStateService.apply() de gedeelde ApplicationUserService.
// domainUserData/projectMemberData overschrijven, en las een andere, gelijktijdig gemounte pagina
// zonder eigen dauGuid (bv. Home, via ActivatedRouteService.routeKeys' fallback) daarna per ongeluk
// die domain-0-guid — precies de oorzaak van een Home-tabstrook die plotseling "niet
// geautoriseerd" leek vlak vóór het navigeren naar AdminPanel. Claims/userData van de response
// blijven gewoon toegepast (met de eigen dGuid/oGuid van déze response, expliciet meegegeven).
export const API_SUPPRESS_DOMAIN_SCOPE_HEADER = 'X-Suppress-Domain-Scope';

// Opt-in header voor achtergrond-aanroepen (polling, capability-probes) die de footer-progressbar
// NIET mogen laten verschijnen — zie RequestLoadingService. Puur client-side signaal, nooit door de
// backend gelezen.
export const API_SUPPRESS_PROGRESS_HEADER = 'X-Suppress-Progress';

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    private readonly __authService = injectRequired(AuthService);
    private readonly __configService = injectRequired(ConfigService);
    private readonly __errorReporting = injectRequired(ErrorReportingService);
    private readonly __requestLoadingService = injectRequired(RequestLoadingService);
    private readonly __responseStateService = injectRequired(ResponseStateService);
    private readonly __client: Client<paths>;

    // Verzoeken (openapi-fetch request-id) die bij de progressbar zijn aangemeld. Afmelden via deze
    // set maakt het idempotent: precies één remove per add, ook als zowel onResponse als onError
    // zouden vuren.
    private readonly __progressRequestIds = new Set<string>();

    private __progress_end(id: string): void {
        if (this.__progressRequestIds.delete(id)) this.__requestLoadingService.remove();
    }

    // > 0 zolang background() loopt — zie daar.
    private __backgroundDepth = 0;

    // Voert fn uit als achtergrondwerk: elk verzoek dat fn SYNCHROON start (GET/POST/... aanroepen
    // starten de fetch meteen, eager promise) krijgt API_SUPPRESS_PROGRESS_HEADER mee, zodat de
    // footer-progressbar er niet op wacht. Bedoeld voor prefetches waar de pagina niet op wacht (bv.
    // de kandidatenlijst van een checklist buiten edit-modus), zonder dat iedere _list_get$ zelf de
    // header hoeft mee te geven. Verzoeken die fn pas later (async) start, vallen er niet onder.
    background<T>(fn: () => T): T {
        this.__backgroundDepth++;
        try {
            return fn();
        } finally {
            this.__backgroundDepth--;
        }
    }

    private __options_get(options: any): any {
        if (this.__backgroundDepth === 0) return options;

        const __headers = options?.headers;
        if (__headers instanceof Headers) {
            const __next = new Headers(__headers);
            __next.set(API_SUPPRESS_PROGRESS_HEADER, 'true');
            return { ...options, headers: __next };
        }
        return { ...options, headers: { ...(__headers ?? {}), [API_SUPPRESS_PROGRESS_HEADER]: 'true' } };
    }

    GET!: Client<paths>['GET'];
    POST!: Client<paths>['POST'];
    PUT!: Client<paths>['PUT'];
    PATCH!: Client<paths>['PATCH'];
    DELETE!: Client<paths>['DELETE'];

    // In-flight-dedupe voor mutaties (POST/PUT/PATCH/DELETE — GET blijft ongemoeid, dat zijn
    // idempotente reads zonder concurrency-risico). Zonder dit kon een dubbelklik of dubbele
    // form-submit dezelfde PATCH/POST bijna gelijktijdig twee keer afvuren; bij een entiteit met een
    // rowversion-kolom (TimeStamp) botst de tweede save dan op de rowversion van vóór de eerste en
    // gooit de server een DbUpdateConcurrencyException (zie PhotoController.MainPhoto — dubbelklik op
    // de hoofdfoto-checkbox). Eén identieke aanroep (zelfde methode+url+params+body) die al onderweg
    // is krijgt nu dezelfde promise terug i.p.v. een nieuwe HTTP-call te starten.
    private readonly __inFlightMutations = new Map<string, Promise<unknown>>();

    private __dedupeMutation<T>(method: string, url: unknown, options: any, exec: () => Promise<T>): Promise<T> {
        // FormData (foto-upload) is niet zinvol te serialiseren als dedupe-key (geen enumerable
        // eigen properties, JSON.stringify geeft altijd "{}") en een batch-upload vuurt bewust
        // meerdere verschillende bestanden naar dezelfde URL — dus nooit dedupen.
        if (options?.body instanceof FormData) return exec();

        const __key = `${method} ${String(url)} ${JSON.stringify(options?.params ?? null)} ${JSON.stringify(options?.body ?? null)}`;
        const __existing = this.__inFlightMutations.get(__key);
        if (__existing) return __existing as Promise<T>;

        const __promise = exec().finally(() => {
            if (this.__inFlightMutations.get(__key) === __promise) this.__inFlightMutations.delete(__key);
        });
        this.__inFlightMutations.set(__key, __promise);
        return __promise;
    }

    constructor() {
        const __baseUrl = this.__configService.originApi$$() ?? '';
        this.__client = createClient<paths>({ baseUrl: __baseUrl });

        const __middleware: Middleware = {
            onRequest: ({ request, id }) => {
                const __token = this.__authService.Token;
                if (__token) {
                    request.headers.set('Authorization', `Bearer ${__token}`);
                }
                // Footer-progressbar: elk verzoek telt mee, behalve achtergrond-aanroepen (opt-out header).
                if (!request.headers.has(API_SUPPRESS_PROGRESS_HEADER)) {
                    this.__progressRequestIds.add(id);
                    this.__requestLoadingService.add();
                }
                return request;
            },
            onResponse: async ({ request, response, id }) => {
                try {
                    return await this.__response_handle(request, response);
                } finally {
                    this.__progress_end(id);
                }
            },
            onError: ({ request, error, id }) => {
                this.__progress_end(id);
                // fetch rejecteert (TypeError) vóór een Response: server/netwerk onbereikbaar.
                // Als status-0 door dezelfde funnel sturen → missingNetworkConnection-notificatie.
                // Suppress-header ook hier respecteren: achtergrond-probes die bij een netwerkfout
                // opnieuw proberen (AdminPanel-probe in ApplicationUserService) zouden anders bij
                // elke poging een nieuwe banner tonen.
                this.__errorReporting.report(new HttpErrorResponse({
                    error,
                    status:     0,
                    statusText: 'Network Error',
                }), { suppressNotification: request.headers.has(API_SUPPRESS_ERROR_HEADER) });
                // void teruggeven: de oorspronkelijke fout blijft doorlopen naar de aanroeper.
            }
        };
        this.__client.use(__middleware);

        // Options via __options_get: binnen background() krijgt het verzoek de progress-opt-out-header.
        this.GET    = ((url: unknown, options: any) => this.__client.GET(url as any, this.__options_get(options))) as Client<paths>['GET'];
        this.POST   = ((url: unknown, options: any) => this.__dedupeMutation('POST', url, options, () => this.__client.POST(url as any, this.__options_get(options)))) as Client<paths>['POST'];
        this.PUT    = ((url: unknown, options: any) => this.__dedupeMutation('PUT', url, options, () => this.__client.PUT(url as any, this.__options_get(options)))) as Client<paths>['PUT'];
        this.PATCH  = ((url: unknown, options: any) => this.__dedupeMutation('PATCH', url, options, () => this.__client.PATCH(url as any, this.__options_get(options)))) as Client<paths>['PATCH'];
        this.DELETE = ((url: unknown, options: any) => this.__dedupeMutation('DELETE', url, options, () => this.__client.DELETE(url as any, this.__options_get(options)))) as Client<paths>['DELETE'];
    }

    // Response-verwerking (state-apply bij succes, fout-funnel bij een non-ok status) — uit onResponse
    // gehaald zodat de progressbar-afmelding daar in één try/finally kan.
    private async __response_handle(request: Request, response: Response): Promise<Response> {
        let __body: unknown = null;
        try {
            __body = await response.clone().json();
        } catch {
            try { __body = await response.clone().text(); } catch { /* ignore */ }
        }

        if (response.ok) {
            if (!request.headers.has(API_SUPPRESS_STATE_APPLY_HEADER)) {
                this.__responseStateService.apply(__body as IResponseData, {
                    suppressDomainScope: request.headers.has(API_SUPPRESS_DOMAIN_SCOPE_HEADER),
                });
            }
        } else {
            // Altijd rapporteren (logging + dev-overlay bij onverwachte 5xx blijven zo
            // altijd zichtbaar) — de suppress-header onderdrukt alleen de vertaalde
            // gebruikersnotificatie, voor aanroepers die een verwachte foutstatus
            // bewust niet als banner willen tonen (zie ErrorReportingService.report).
            this.__errorReporting.report(new HttpErrorResponse({
                error:      __body,
                status:     response.status,
                statusText: response.statusText,
                url:        response.url,
            }), { suppressNotification: request.headers.has(API_SUPPRESS_ERROR_HEADER) });
        }
        return response;
    }
}
